export const POLAND_TIME_ZONE = 'Europe/Warsaw';

const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})?$/;

const dateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: POLAND_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});

const getParts = (date) => Object.fromEntries(
  dateTimeFormatter.formatToParts(date)
    .filter(({ type }) => type !== 'literal')
    .map(({ type, value }) => [type, value])
);

const getWarsawOffsetMs = (date) => {
  const parts = getParts(date);
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second) - date.getTime();
};

// Converts a UTC API date-time to a Polish local value for date/time controls.
export const utcToPolishLocalDateTime = (value) => {
  if (typeof value !== 'string' || !ISO_DATE_TIME.test(value)) return value;

  const date = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return value;

  const parts = getParts(date);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
};

// Converts a Polish local value to a canonical UTC API date-time.
export const polishLocalDateTimeToUtc = (value) => {
  if (typeof value !== 'string') return value;

  const match = value.match(ISO_DATE_TIME);
  if (!match) return value;

  if (match[8]) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toISOString();
  }

  const [, year, month, day, hour, minute, second = '00', fraction = ''] = match;
  const utcGuess = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second, +(fraction.padEnd(3, '0') || 0));
  let timestamp = utcGuess - getWarsawOffsetMs(new Date(utcGuess));
  timestamp = utcGuess - getWarsawOffsetMs(new Date(timestamp));
  return new Date(timestamp).toISOString();
};

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]';

export const mapDateTimes = (value, mapper, seen = new WeakMap()) => {
  if (typeof value === 'string') return mapper(value);
  if (value === null || typeof value !== 'object' || value instanceof Date || value instanceof FormData || value instanceof Blob) return value;
  if (seen.has(value)) return seen.get(value);

  if (Array.isArray(value)) {
    const mapped = [];
    seen.set(value, mapped);
    value.forEach((item, index) => { mapped[index] = mapDateTimes(item, mapper, seen); });
    return mapped;
  }

  if (!isPlainObject(value)) return value;
  const mapped = {};
  seen.set(value, mapped);
  Object.entries(value).forEach(([key, item]) => { mapped[key] = mapDateTimes(item, mapper, seen); });
  return mapped;
};
